import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Youtube, ArrowUpRight, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNEL_ID = "UCxvmL3w8S2e6tTPN18sE5Fg";
const CHANNEL_URL = `https://www.youtube.com/channel/${CHANNEL_ID}`;
const SHORTS_URL = `${CHANNEL_URL}/shorts`;

const FALLBACK_LONG = "in20_cl-j3c";
const FALLBACK_SHORT = "in20_cl-j3c";

function buildEmbedSrc(videoId, loop = false) {
  const base = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&enablejsapi=1&mute=1&autoplay=1&playsinline=1`;
  return loop ? `${base}&loop=1&playlist=${videoId}` : base;
}

function MuteButton({ muted, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={muted ? "Unmute" : "Mute"}
      aria-pressed={!muted}
      data-testid={testId}
      className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 px-3.5 py-2 bg-black/70 backdrop-blur-md border border-white/15 hover:border-gold hover:text-gold text-zinc-100 text-[11px] uppercase tracking-overline transition-colors"
    >
      {muted ? (
        <>
          <VolumeX className="w-4 h-4" strokeWidth={1.75} />
          Unmute
        </>
      ) : (
        <>
          <Volume2 className="w-4 h-4 text-gold" strokeWidth={1.75} />
          Mute
        </>
      )}
    </button>
  );
}

function useLatestVideo(kind, fallback) {
  const [vid, setVid] = useState(fallback);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/latest-video?kind=${kind}`)
      .then((r) => {
        const id = r?.data?.video_id;
        if (!cancelled && id && /^[A-Za-z0-9_-]{11}$/.test(id)) setVid(id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [kind]);
  return vid;
}

export default function MediaHub() {
  const longId = useLatestVideo("long", FALLBACK_LONG);
  const shortId = useLatestVideo("short", FALLBACK_SHORT);

  const [longMuted, setLongMuted] = useState(true);
  const [shortMuted, setShortMuted] = useState(true);

  const longRef = useRef(null);
  const shortRef = useRef(null);

  const post = (iframe, cmd) => {
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: cmd, args: [] }),
      "*",
    );
  };

  const toggleLong = () => {
    post(longRef.current, longMuted ? "unMute" : "mute");
    setLongMuted((m) => !m);
  };
  const toggleShort = () => {
    post(shortRef.current, shortMuted ? "unMute" : "mute");
    setShortMuted((m) => !m);
  };

  return (
    <section
      id="live"
      data-testid="media-hub-section"
      className="py-20 md:py-28 px-6 md:px-12 bg-black"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold" />
            <span className="text-xs uppercase tracking-overline text-zinc-500">
              01 — From the Road
            </span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            The latest episode,{" "}
            <em className="italic text-gold">and the latest moment</em>.
          </h2>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-[1.65] font-light">
            Two feeds, auto-updated as we publish — the newest long-form
            Expedition on the left, the freshest YouTube Short on the right.
          </p>
        </div>

        {/* Side-by-side: 16:9 long-form (left, 2fr) + 9:16 short (right, 1fr) on desktop.
            Stacks vertically on mobile (16:9 first, 9:16 second). */}
        <div
          className="grid gap-6 md:gap-8 reveal"
          style={{ gridTemplateColumns: "1fr" }}
        >
          <div className="grid gap-6 md:gap-8 md:grid-cols-[2fr_1fr] items-start">
            {/* LEFT — Main Story (16:9) */}
            <div className="space-y-4">
              <div
                className="relative bg-black border border-white/10 overflow-hidden w-full"
                style={{ aspectRatio: "16 / 9" }}
              >
                <iframe
                  ref={longRef}
                  key={longId}
                  src={buildEmbedSrc(longId, false)}
                  title="Latest long-form Expedition"
                  data-testid="hub-long-iframe"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                <MuteButton
                  muted={longMuted}
                  onClick={toggleLong}
                  testId="hub-long-mute-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-overline text-zinc-500">
                  Latest Expedition
                </span>
                <a
                  href={CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hub-channel-btn"
                  className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-overline text-zinc-400 hover:text-gold transition-colors"
                >
                  <Youtube className="w-4 h-4" strokeWidth={1.5} />
                  All Episodes
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>

            {/* RIGHT — Moments (9:16) */}
            <div className="space-y-4">
              <div
                className="relative bg-black border border-white/10 overflow-hidden mx-auto w-full max-w-[360px] md:max-w-none"
                style={{ aspectRatio: "9 / 16" }}
              >
                <iframe
                  ref={shortRef}
                  key={shortId}
                  src={buildEmbedSrc(shortId, true)}
                  title="Latest YouTube Short"
                  data-testid="hub-short-iframe"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                <MuteButton
                  muted={shortMuted}
                  onClick={toggleShort}
                  testId="hub-short-mute-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-overline text-zinc-500">
                  Latest Short
                </span>
                <a
                  href={SHORTS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hub-shorts-btn"
                  className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-overline text-zinc-400 hover:text-gold transition-colors"
                >
                  <Youtube className="w-4 h-4" strokeWidth={1.5} />
                  All Shorts
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
